import { ChronoUnit, ZonedDateTime } from '@js-joda/core';
import { useEffect, useState } from 'react';

type Props = {
  endTime?: string | ZonedDateTime | null;
  startTime?: string | ZonedDateTime | null;
  joinableTime?: string | ZonedDateTime | null;
};

export default ({ endTime, startTime, joinableTime }: Props): boolean => {
  const [isJoinable, setIsJoinable] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    // Use a reasonable timeout limit for cascading - 1 day is much larger than
    // the 15-minute join window but well within setTimeout's safe range
    const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 1 day in milliseconds

    const scheduleJoinableCheck = () => {
      const now = ZonedDateTime.now();
      if (!endTime || !startTime || !joinableTime) {
        return;
      }

      const classStart =
        typeof startTime === 'string'
          ? ZonedDateTime.parse(startTime)
          : startTime;
      const classEnd =
        typeof endTime === 'string' ? ZonedDateTime.parse(endTime) : endTime;

      const parsedJoinableTime =
        typeof joinableTime === 'string'
          ? ZonedDateTime.parse(joinableTime)
          : joinableTime;

      const isInProgress = now.isAfter(classStart) && now.isBefore(classEnd);
      const isEnded = now.isAfter(classEnd);

      if (isInProgress) {
        setIsJoinable(true);
      } else if (!isEnded) {
        const timeTilJoinable = now.until(
          parsedJoinableTime,
          ChronoUnit.MILLIS,
        );

        if (timeTilJoinable <= ONE_DAY_MS) {
          // Normal case: can set timeout directly
          timer = setTimeout(() => {
            setIsJoinable(true);
          }, timeTilJoinable);
        } else {
          // Long delay case: schedule next check when we're within the timeout limit
          timer = setTimeout(scheduleJoinableCheck, ONE_DAY_MS);
        }
      }
    };

    scheduleJoinableCheck();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [startTime, endTime, joinableTime]);

  return isJoinable;
};
